/**
 * Empty state. Screen spec 4.6.
 *
 * A restrained line-art mark, one sentence, one action. Never more than one
 * action, and the action is never "Learn more". An empty screen with three
 * choices on it is a screen that does not know what the person should do next.
 *
 * The marks are 24px stroke geometry drawn inline from tokens: no illustration
 * style, no character, no color, no external asset. They are furniture that
 * says "this container is empty on purpose", and nothing more.
 *
 * One empty state in the product carries an agent prompt instead of a plain
 * action, because the honest answer to "there are no tables yet" is that the
 * person's coding agent creates them, not this console.
 */

import type { ReactNode } from 'react';
import { CopyField } from './CopyField';
import { t } from '../../lib/i18n/t';
import '../../styles/components/empty.css';

export type EmptyMark = 'box' | 'table' | 'file' | 'key' | 'clock' | 'chart' | 'clear';

const MARKS: Record<EmptyMark, ReactNode> = {
  box: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="1.5" />
      <path d="M3 10h18" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="5" width="18" height="15" rx="1.5" />
      <path d="M3 10h18M9 10v10M15 10v10" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9M18 12v3" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.5L15.5 15" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V9M10 20v-6M16 20v-9M22 20H2" />
    </>
  ),
  clear: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12.5 11 15.5l5.5-6" />
    </>
  ),
};

export interface EmptyStateProps {
  mark?: EmptyMark;
  /** One sentence. The whole message. */
  sentence: string;
  /** One optional extra line of context. Never two. */
  context?: string;
  /** The single action. Reassurance states pass nothing, on purpose. */
  action?: ReactNode;
  /** The agent prompt variant: a copyable literal instead of a console action. */
  agentPrompt?: string;
  agentPromptLabel?: string;
}

export function EmptyState({
  mark = 'box',
  sentence,
  context,
  action,
  agentPrompt,
  agentPromptLabel,
}: EmptyStateProps) {
  return (
    <div className="empty">
      <span className="empty__mark" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          {MARKS[mark]}
        </svg>
      </span>

      <p className="empty__say">{sentence}</p>
      {context ? <p className="empty__context">{context}</p> : null}

      {agentPrompt ? (
        <div className="empty__prompt">
          <p className="empty__prompt-label">
            {agentPromptLabel ?? t('Paste this to your coding agent')}
          </p>
          <CopyField label={t('Prompt')} value={agentPrompt} />
        </div>
      ) : null}

      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}
